/* eslint-disable max-lines */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { setDigitalData } from 'mf_core/adobe';
import { useLocalization, useTranslate } from '@im/react-localization';
import { ObjectMap } from 'mf_core/query';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import CategoryInsightsDetails from './CategoryInsightsDetails';
import { useInsightQueryMutation } from '../../hooks/useInsights';
import { combinedInsights } from './InsightsQueryRequestBody';
import { useExtendedSummaryMutation } from '../../hooks/use-productsummary';
import { InsightsMessagingQueueSkeleton } from './InsightsMessagingQueueSkeleton';
import {
  GetUserRole,
  getSKUMap,
  sortJSONByNullValue,
  sortJSONListByBooleanKey,
  sortJSONListByKey,
  sortJSONListByTwoBooleanKey,
  userRoles,
} from '../../utils/common.util';
import { useFeatureFlag } from 'mf_core/feature-flag';

type Props = {
  chipsDetails: ObjectMap[];
  currentPage: string;
  insightsDetails: ObjectMap | null;
  maxDropHeight?: number;
  setChipsDetails: (chipDetails: ObjectMap[]) => void;
  setInsightsDetails: (insightsDetails: ObjectMap | null) => void;
  toggleInsightsPopup?: () => void;
};

type InsightCategories = {
  accountInfo: string;
  complementaryProducts: string;
  efficientTips: string;
  trendingCategory: string;
  trendingProduct: string;
  trendingProductCategory: string;
  trendingProductSearch: string;
  trendingProductVendor: string;
  trendingVendor: string;
};

export default function InsightsContent(props: Props) {
  const {
    chipsDetails,
    currentPage,
    insightsDetails,
    maxDropHeight,
    setChipsDetails,
    setInsightsDetails,
  } = props;

  const [insightsMessages, setInsightsMessages] = useState<any>();
  const [insightsMessagesLoading, setInsightsMessagesLoading] = useState(false);
  const insightsMutation = useInsightQueryMutation();

  const dataExtendedMutation = useExtendedSummaryMutation();
  const { translate } = useLocalization();
  const translateKey = useTranslate();
  const userRole = GetUserRole();
  const {
    isReady: enabledInsightsVersionIsReady,
    value: enabledInsightsVersion,
  } = useFeatureFlag('EnabledInsightsVersion');

  const {
    isReady: enableEfficiencyTipsInsightsIsReady,
    value: enableEfficiencyTipsInsights,
  } = useFeatureFlag('EnableEfficiencyTipsInsights');

  const { isReady: isConfigureToOrderUrlReady, value: configureToOrderUrl } =
    useFeatureFlag('ConfigureToOrderUrl');

  const {
    isReady: isConfigureToOrderQuestionsUrlReady,
    value: configureToOrderQuestionsUrl,
  } = useFeatureFlag('ConfigureToOrderQuestionsUrl');

  const { value: enableInsightsSearchURLRefinement } = useFeatureFlag(
    'EnableInsightsSearchURLRefinement'
  );

  const { value: enableUserManagement } = useFeatureFlag(
    'EnableUserManagement'
  );

  const categoryKeys: InsightCategories = useMemo(() => {
    return {
      accountInfo: 'Account Info',
      complementaryProducts: 'Complementary Products',
      efficientTips: 'Efficient Tips',
      trendingCategory: 'Trending Category',
      trendingProduct: 'Trending Product',
      trendingProductCategory: 'Trending Product Category',
      trendingProductSearch: 'Trending Product Search',
      trendingProductVendor: 'Trending Product Vendor',
      trendingVendor: 'Trending Vendor',
    };
  }, []);

  const [productSummaryDependentInsights, setProductSummaryDependentInsights] =
    useState({
      missingProductInsight: true,
      topCategoryInsight: true,
      topVendorInsight: true,
      trendingProductInsight: true,
    });
  const [allInsightsLoaded, setAllInsightsLoaded] = useState(false);

  const pageWiseCagetoriesDetails = useMemo(() => {
    return {
      'Details Page': [
        categoryKeys.efficientTips,
        categoryKeys.trendingCategory,
        categoryKeys.complementaryProducts,
      ],
      'Home Page': [
        categoryKeys.efficientTips,
        categoryKeys.trendingProduct,
        categoryKeys.trendingVendor,
        categoryKeys.accountInfo,
        categoryKeys.trendingCategory,
        categoryKeys.trendingProductSearch,
        categoryKeys.complementaryProducts,
      ],
      'Insights Center': [
        categoryKeys.efficientTips,
        categoryKeys.trendingProduct,
        categoryKeys.trendingVendor,
        categoryKeys.accountInfo,
        categoryKeys.trendingCategory,
        categoryKeys.trendingProductSearch,
        categoryKeys.complementaryProducts,
        categoryKeys.trendingProductVendor,
        categoryKeys.trendingProductCategory,
      ],
      'Product Category': [
        categoryKeys.efficientTips,
        categoryKeys.trendingVendor,
        categoryKeys.trendingCategory,
      ],
      'Search Page': [
        categoryKeys.efficientTips,
        categoryKeys.trendingProduct,
        categoryKeys.trendingProductSearch,
      ],
      'Vendor List': [categoryKeys.efficientTips, categoryKeys.trendingVendor],
      'cart checkout': [categoryKeys.efficientTips, categoryKeys.accountInfo],
    } as ObjectMap;
  }, [categoryKeys]);

  const styledLink = `<style> 
      .styledLink {
        color: #0077D4;
        background-color: transparent;
        text-decoration: none;
        font-weight: 600;
      };
    </style>
   `;

  const tabContentView = useCallback(
    (categoryInsightData: Array<ObjectMap>) => {
      return (
        <CategoryInsightsDetails
          maxDropHeight={maxDropHeight}
          topInsightsData={categoryInsightData}
        />
      );
    },
    [maxDropHeight]
  );

  const getCategoryDetailsBasedOnPage = useCallback(
    (
      currentPage: string,
      insightCategoriesList: any[],
      insightsData: { [x: string]: any[] }
    ) => {
      const filteredCategoryWiseInsights: ObjectMap = {};

      insightCategoriesList?.forEach((category: any) => {
        let filteredInsights: ObjectMap[] = [];
        insightsData[category ?? '']?.forEach((insightData: any) => {
          if (insightData?.pages?.includes(currentPage))
            filteredInsights = [...filteredInsights, insightData];
        });

        filteredCategoryWiseInsights[category] = filteredInsights;
      });

      return filteredCategoryWiseInsights;
    },
    []
  );

  const getAllCategoriesInsights = useCallback(
    (insightCategoriesList: any[], insightsData: { [x: string]: any }) => {
      let allCategoriesInsights: Array<ObjectMap> = [];

      insightCategoriesList?.forEach((category: any) => {
        allCategoriesInsights = [
          ...allCategoriesInsights,
          ...[...(insightsData[category ?? ''] ?? [])],
        ];
      });

      return allCategoriesInsights;
    },
    []
  );

  const updateChipsDetails = useCallback(
    (
      navigatedPage: string,
      insightCategoriesList: string[],
      categoryWiseInsightsData: any
    ) => {
      const updatedCategoryInsights = getCategoryDetailsBasedOnPage(
        navigatedPage,
        insightCategoriesList,
        categoryWiseInsightsData
      );
      const allCategoriesInsights = getAllCategoriesInsights(
        insightCategoriesList,
        updatedCategoryInsights
      );

      const chipsList: Array<ObjectMap> = [
        {
          active: true,
          content: tabContentView(allCategoriesInsights),
          dataName: 'Quick-insights_Popup_View-All',
          digitalData: allCategoriesInsights?.map((insight: ObjectMap) => {
            return {
              InsightTab: 'View All',
              ...insight?.digitalData,
            };
          }),
          insightsCount: String(allCategoriesInsights?.length ?? 0),
          title: translate('Insights.ViewAll'),
        },
      ];

      insightCategoriesList?.forEach((category: string) => {
        if (
          updatedCategoryInsights?.[category] &&
          updatedCategoryInsights?.[category]?.length > 0
        ) {
          chipsList.push({
            active: false,
            content: tabContentView(updatedCategoryInsights?.[category] ?? []),
            dataName: `Quick-insights_Popup_${category?.replace(/\s+/g, '-')}`,
            digitalData: updatedCategoryInsights?.[category]?.map(
              (insight: ObjectMap) => {
                return {
                  InsightTab: category,
                  ...insight?.digitalData,
                };
              }
            ),
            insightsCount: String(
              updatedCategoryInsights?.[category]?.length ?? '0'
            ),
            title: translate(`Insights.${category.replace(/\s/g, '')}`),
          });
        }
      });

      setChipsDetails(chipsList);
    },
    [
      getCategoryDetailsBasedOnPage,
      getAllCategoriesInsights,
      tabContentView,
      translate,
      setChipsDetails,
    ]
  );

  const showContent = useCallback(
    (selectedChipDetails: ObjectMap) => {
      const tempChipDetails = chipsDetails?.map((chipDetails) => {
        if (selectedChipDetails?.title === chipDetails?.title) {
          return {
            ...chipDetails,
            active: true,
          };
        } else {
          return {
            ...chipDetails,
            active: false,
          };
        }
      });
      setChipsDetails(tempChipDetails);

      setDigitalData({
        QuickInsightsImpressions: selectedChipDetails.digitalData ?? [],
      });
    },
    [chipsDetails, setChipsDetails]
  );

  const onCountClick = useCallback(() => {
    // On count click handler
  }, []);

  const getSalesFrequencyInsight = useCallback(
    (orderDetails: ObjectMap) => {
      if (orderDetails?.order_count) {
        const orderHistory = `/cep/app/frame?url=${encodeURIComponent(
          '/spa/ostordersearchcep/#/orders'
        )}`;

        const heading = translate('InsightCharts.SalesTrend');
        const data = (
          <>
            {translate(
              'Insights.OrderVolume_CardContent',
              {
                history: `${styledLink}<a class='styledLink' data-recommendationrecordid = ${
                  insightsMessages?.recommendationRecordId
                } 
                data-name ='Quick-insights_Popup_Sales-trend_History' data-linktype = 'sales'  data-insight = 'Sales trend'
                 href=${orderHistory}>${translateKey('Insights.History')}</a>`,
                orderCount: `${styledLink}<a class='styledLink' data-recommendationrecordid = ${
                  insightsMessages?.recommendationRecordId
                } data-name ='Quick-insights_Popup_Sales-trend_${
                  orderDetails?.order_count ?? 0
                }' data-linktype = 'sales' 
                data-insight = 'Sales trend'
                href=${orderHistory}>${orderDetails?.order_count ?? 0}
                </a>`,
              },
              true
            )}
          </>
        );

        return {
          category: categoryKeys.accountInfo,
          description: data,
          digitalData: {
            InsightCategory: [],
            InsightName: 'Sales trend',
            InsightOrderhistory: orderDetails?.order_count,
            InsightProduct: [],
            InsightSearchTerms: [],
            InsightVendor: [],
          },
          pages: 'Home Page, Insights Center, cart checkout',
          title: heading,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  const getCategoryByVendorInsight = useCallback(
    (vendorDetails: ObjectMap) => {
      const categoryName = vendorDetails?.category ?? '';
      const category = toPascal(categoryName);
      const encodedCategoryName = encodeURIComponent(categoryName);

      const vendorName = vendorDetails?.vendor_name ?? '';
      const vendor = toPascal(vendorName);
      const encodedVendorName = encodeURIComponent(vendorName);

      if (categoryName && vendorName) {
        const vendorUrl =
          enableInsightsSearchURLRefinement === 'true'
            ? `/cep/app/product/productsearch?vendorname=VEN_REFINE||eq||${encodedVendorName}||false||&categories=CAT_REFINE||eq||${encodedCategoryName}||false||&displaytitle=${encodedVendorName}&keywords=${encodedVendorName}`
            : `/cep/app/product/productsearch?displaytitle=${encodedVendorName}&keywords=${encodedVendorName}`;

        const categoryUrl =
          enableInsightsSearchURLRefinement === 'true'
            ? `/cep/app/product/productsearch?categories=CAT_REFINE||eq||${encodedCategoryName}||false||&vendorname=VEN_REFINE||eq||${encodedVendorName}||false||&displaytitle=${encodedCategoryName}&keywords=${encodedCategoryName}`
            : `/cep/app/product/productsearch?displaytitle=${encodedCategoryName}&keywords=${encodedCategoryName}`;

        const heading = translate(
          enabledInsightsVersion === 'v4'
            ? 'Insights.VendorTrends'
            : 'InsightCharts.CategoriesByVendors'
        );
        const dataTagName =
          enabledInsightsVersion === 'v4'
            ? 'VendorTrends'
            : 'Categoriesbyvendors';
        const data = (
          <>
            {translate(
              enabledInsightsVersion === 'v4'
                ? 'Insights.VendorTrendsCard'
                : 'Insights.TopVendorInTopCategory_CardContent',
              {
                category: `${styledLink}<a data-recommendationrecordid=${
                  insightsMessages?.recommendationRecordId
                }
                data-linktype = 'productCategory'
                data-insight = "${
                  enabledInsightsVersion === 'v4'
                    ? 'Vendor Trends'
                    : 'Categories By Vendors'
                }"
                data-name="Quick-insights_Popup_${dataTagName}_${category?.replace(
                  /\s+/g,
                  '-'
                )}"
                ' class='styledLink' href="${categoryUrl}">${category} </a>`,
                vendor: `${styledLink}<a class='styledLink' 
                data-recommendationrecordid=${
                  insightsMessages?.recommendationRecordId
                } 
                data-linktype = 'vendor'
                data-insight = "${
                  enabledInsightsVersion === 'v4'
                    ? 'Vendor Trends'
                    : 'Categories By Vendors'
                }"
                data-name="Quick-insights_Popup_${dataTagName}_${vendor?.replace(
                  /\s+/g,
                  '-'
                )}" href="${vendorUrl}">${vendor}</a>`,
              },
              true
            )}
          </>
        );

        return {
          category: categoryKeys.trendingVendor,
          description: data,
          digitalData: {
            InsightCategory: [vendorDetails?.category],
            InsightName:
              enabledInsightsVersion === 'v4'
                ? 'Vendor trends'
                : 'Categories by vendors',
            InsightOrderhistory: '',
            InsightProduct: [],
            InsightSearchTerms: [],
            InsightVendor: [vendorDetails?.vendor_name],
          },
          pages: 'Home Page, Insights Center, Vendor List, Product Category',
          title: heading,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  const updateTopVendorInsight = useCallback(
    (top10VendorsDetails: ObjectMap[]) => {
      if (
        Array.isArray(top10VendorsDetails) &&
        top10VendorsDetails?.length > 0
      ) {
        let partNumbers = '';

        top10VendorsDetails?.forEach((vendorDetails: ObjectMap) => {
          const partNumber = vendorDetails?.sku?.substring(2) ?? '';
          partNumbers += `{'partNumber':'${partNumber}'},`;
        });

        const skusList = `[${partNumbers.slice(0, -1)}]`;

        if (skusList === '[]') {
          setInsightsDetails((prevInsightsDetails: ObjectMap) => {
            return {
              ...prevInsightsDetails,
              [categoryKeys.trendingProductVendor]: [],
            };
          });

          return;
        }

        dataExtendedMutation
          .mutateAsync(skusList)
          .then((productSummaryResponse: any) => {
            const productSummaries = productSummaryResponse?.summaries;

            if (!productSummaries) {
              setInsightsDetails((prevInsightsDetails: ObjectMap) => {
                return {
                  ...prevInsightsDetails,
                  [categoryKeys.trendingProductVendor]: [],
                };
              });

              return;
            }

            const skusMap: ObjectMap = getSKUMap(productSummaries);
            const filteredProductDetails: Array<ObjectMap> = [];

            top10VendorsDetails?.forEach((vendorDetails: ObjectMap) => {
              const partNumber = vendorDetails?.sku
                ?.substring(2)
                .replace(/^0+/, '');

              if (Object.prototype.hasOwnProperty.call(skusMap, partNumber)) {
                filteredProductDetails.push({
                  ...vendorDetails,
                  isAuthorizedToBuy: skusMap[partNumber]?.authorizedToPurchase,
                  productSummary: skusMap[partNumber],
                });
              }
            });

            const sortedCategoryDetailsByRank = sortJSONListByKey(
              filteredProductDetails,
              'sku_rank'
            );
            const sortedDataByAuthorizationToBuy = sortJSONListByBooleanKey(
              sortedCategoryDetailsByRank,
              'isAuthorizedToBuy',
              'desc'
            );

            if (sortedDataByAuthorizationToBuy?.length === 0) {
              setInsightsDetails((prevInsightsDetails: ObjectMap) => {
                return {
                  ...prevInsightsDetails,
                  [categoryKeys.trendingProductVendor]: [],
                };
              });

              return;
            }

            const topVendorsDetails = sortedDataByAuthorizationToBuy?.[0] ?? {};
            const partNumber = topVendorsDetails?.productSummary?.partnumber;
            const heading =
              enabledInsightsVersion === 'v4'
                ? translate('Insights.ProductDemand')
                : translate('InsightCharts.TopVendors');
            const productDetailsLink = `/cep/app/product/productdetails?id=${partNumber}`;

            const vendorName = topVendorsDetails?.vendor_name ?? '';
            const vendor = toPascal(vendorName);
            const encodedVendorName = encodeURIComponent(vendorName);

            const searchLink = `/cep/app/product/productsearch?displaytitle=${encodedVendorName}&keywords=${encodedVendorName}`;

            const data = (
              <Stack alignItems="start" direction="row" spacing={1.5}>
                <Box sx={{ lineHeight: '25px', overflow: 'hidden' }}>
                  {enabledInsightsVersion === 'v4' ? (
                    <Typography
                      component={'span'}
                      sx={{ fontSize: '16px', lineHeight: '24px' }}
                    >
                      {translate(
                        'Insights.TopVendorAndProduct_CardContent',
                        {
                          product: `${styledLink}<a class='styledLink'
                  data-recommendationrecordid=${
                    insightsMessages?.recommendationRecordId
                  }  data-name=${`Insight_TopVendors Product_${topVendorsDetails?.product_description?.replace(
                            /\s+/g,
                            '-'
                          )}`}
                          data-linktype = 'productName'
                          data-insight = "${
                            enabledInsightsVersion === 'v4'
                              ? 'Product demand'
                              : 'Vendor trends'
                          }"

                href=${productDetailsLink}>${
                            topVendorsDetails?.product_description
                          }</a>`,

                          vendor: `${styledLink}<a class='styledLink'
                  data-recommendationrecordid=${
                    topVendorsDetails?.recommendationRecordId
                  }    data-linktype = 'vendor'
                  data-insight = "${
                    enabledInsightsVersion === 'v4'
                      ? 'Product demand'
                      : 'Vendor trends'
                  }"
                  data-name=${`Insight_TopVendors Vendor_${vendor}`}
                  href=${searchLink}>${vendor}                  
                  </a>`,
                        },
                        true
                      )}
                    </Typography>
                  ) : (
                    <Typography
                      component={'span'}
                      sx={{ fontSize: '16px', lineHeight: '24px' }}
                    >
                      {translate('HomePage.InsightsVendorGrowth_last30Days')}{' '}
                      {
                        <StyledLinkTypography
                          data-insight={
                            enabledInsightsVersion === 'v4'
                              ? 'Product demand'
                              : 'Vendor trends'
                          }
                          data-linktype="vendor"
                          data-name={`Quick-insights_Popup_Top Vendors_${topVendorsDetails?.vendor_name?.replace(
                            /\s+/g,
                            '-'
                          )}`}
                          data-recommendationrecordid={`${insightsMessages?.recommendationRecordId}`}
                          href={searchLink}
                          isDisabled={false}
                        >
                          {topVendorsDetails?.vendor_name}
                        </StyledLinkTypography>
                      }{' '}
                      {translate(
                        'HomePage.InsightsVendorGrowth_SignificantDemand'
                      )}{' '}
                      {
                        <StyledLinkTypography
                          data-insight={
                            enabledInsightsVersion === 'v4'
                              ? 'Product demand'
                              : 'Vendor trends'
                          }
                          data-linktype="productName"
                          data-name={`Quick-insights_Popup_Top Vendors_${topVendorsDetails?.product_description?.replace(
                            /\s+/g,
                            '-'
                          )}`}
                          data-recommendationrecordid={`${insightsMessages?.recommendationRecordId}`}
                          href={productDetailsLink}
                          isDisabled={false}
                        >
                          {topVendorsDetails?.product_description}
                        </StyledLinkTypography>
                      }{' '}
                    </Typography>
                  )}
                </Box>
              </Stack>
            );

            setInsightsDetails((prevInsightsDetails: ObjectMap) => {
              return {
                ...prevInsightsDetails,
                [categoryKeys.trendingProductVendor]: [
                  {
                    category: categoryKeys.trendingProductVendor,
                    description: data,
                    digitalData: {
                      InsightCategory: [],
                      InsightName:
                        enabledInsightsVersion === 'v4'
                          ? 'Product demand'
                          : 'Top vendors',
                      InsightOrderhistory: '',
                      InsightProduct: [topVendorsDetails?.product_description],
                      InsightSearchTerms: [],
                      InsightVendor: [topVendorsDetails?.vendor_name],
                    },
                    pages: 'Insights Center',
                    title: heading,
                  },
                ],
              };
            });
          })
          .finally(() => {
            setProductSummaryDependentInsights((prevData: any) => {
              return {
                ...prevData,
                topVendorInsight: false,
              };
            });
          });
      } else {
        setProductSummaryDependentInsights((prevData: any) => {
          return {
            ...prevData,
            topVendorInsight: false,
          };
        });
      }
    },
    [
      categoryKeys,
      translate,
      dataExtendedMutation,
      insightsMessages,
      enabledInsightsVersion,
      styledLink,
      setInsightsDetails,
    ]
  );

  const updateEmptyComplementaryProducts = useCallback(() => {
    setInsightsDetails((prevInsightsDetails: ObjectMap) => {
      return {
        ...prevInsightsDetails,
        [categoryKeys.complementaryProducts]: [],
      };
    });
  }, [categoryKeys, setInsightsDetails]);

  const updateMissingProductInBasket = useCallback(
    (missingProductDetails: ObjectMap[]) => {
      if (
        Array.isArray(missingProductDetails) &&
        missingProductDetails?.length > 0
      ) {
        let partNumbers = '';

        missingProductDetails?.forEach((bundle: ObjectMap) => {
          const partNumberA = bundle?.SKUA?.substring(2) ?? '';
          const partNumberB = bundle?.SKUB?.substring(2) ?? '';

          partNumbers += `{'partNumber':'${partNumberA}'},{'partNumber':'${partNumberB}'},`;
        });

        const skusList = `[${partNumbers.slice(0, -1)}]`;
        let topBundle: ObjectMap = {};

        dataExtendedMutation
          .mutateAsync(skusList)
          .then((productSummaryResponse: any) => {
            const productSummaries = productSummaryResponse?.summaries;

            if (!productSummaries) {
              updateEmptyComplementaryProducts();
              return;
            }

            const skusMap: ObjectMap = getSKUMap(productSummaries);
            const filteredBundleDetails: Array<ObjectMap> = [];

            missingProductDetails?.forEach((bundleDetails: ObjectMap) => {
              const partNumberA = bundleDetails?.SKUA?.substring(2);
              const partNumberB = bundleDetails?.SKUB?.substring(2);

              if (
                Object.prototype.hasOwnProperty.call(skusMap, partNumberA) &&
                Object.prototype.hasOwnProperty.call(skusMap, partNumberB)
              ) {
                if (skusMap[partNumberA] && skusMap[partNumberB]) {
                  filteredBundleDetails.push({
                    ...bundleDetails,
                    isAuthorizedToBuySKUA:
                      skusMap[partNumberA]?.authorizedToPurchase,
                    isAuthorizedToBuySKUB:
                      skusMap[partNumberB]?.authorizedToPurchase,
                    productSummaryA: skusMap[partNumberA],
                    productSummaryB: skusMap[partNumberB],
                  });
                }
              }
            });

            const sortedCategoryDetailsByRank = sortJSONListByKey(
              filteredBundleDetails,
              'rank'
            );
            const sortedDataByAuthorizationToBuy = sortJSONListByTwoBooleanKey(
              sortedCategoryDetailsByRank,
              'isAuthorizedToBuySKUA',
              'isAuthorizedToBuySKUB',
              'desc'
            );

            if (sortedDataByAuthorizationToBuy?.length === 0) {
              updateEmptyComplementaryProducts();
              return;
            }

            topBundle = sortedDataByAuthorizationToBuy?.[0] ?? {};

            const heading = translate('Insights.ComplementaryProducts');
            const productAUrl = `/cep/app/product/productdetails?id=${topBundle?.SKUA?.substring(
              2
            )}`;
            const productBUrl = `/cep/app/product/productdetails?id=${topBundle?.SKUB?.substring(
              2
            )}`;
            const data = translate(
              'Insights.MissingProductMarketBasketText',
              {
                productA: productAUrl
                  ? `${styledLink}<a class='styledLink' 
                  data-recommendationrecordid=${
                    insightsMessages?.recommendationRecordId
                  }
                  data-linktype = 'productName'
                  data-insight= 'Complementary products'
                  data-name="Quick-insights_Popup_Categoriesbyvendors_${topBundle?.PRODA_DESCRIPTION?.replace(
                    /\s+/g,
                    '-'
                  )}" 
                  href="${productAUrl}">${topBundle?.PRODA_DESCRIPTION}</a>`
                  : `<b>${topBundle?.PRODA_DESCRIPTION}</b>`,
                productB: productBUrl
                  ? `${styledLink}<a class='styledLink' 
                  data-recommendationrecordid=${
                    insightsMessages?.recommendationRecordId
                  }
                  data-linktype = 'productName'
                  data-insight= 'Complementary products'
                  data-name="Quick-insights_Popup_Categoriesbyvendors_${topBundle?.PRODB_DESCRIPTION?.replace(
                    /\s+/g,
                    '-'
                  )}"
                  href="${productBUrl}">${topBundle?.PRODB_DESCRIPTION}</a>`
                  : `<b>${topBundle?.PRODB_DESCRIPTION}</b>`,
              },
              true
            );

            setInsightsDetails((prevInsightsDetails: ObjectMap) => {
              return {
                ...prevInsightsDetails,
                [categoryKeys.complementaryProducts]: [
                  {
                    category: categoryKeys.complementaryProducts,
                    description: data,
                    digitalData: {
                      InsightCategory: [],
                      InsightName: categoryKeys.complementaryProducts,
                      InsightOrderhistory: '',
                      InsightProduct: [
                        topBundle?.PRODA_DESCRIPTION,
                        topBundle?.PRODB_DESCRIPTION,
                      ],
                      InsightSearchTerms: [],
                      InsightVendor: [],
                    },
                    pages: 'Home Page, Insights Center, Details Page',
                    title: heading,
                  },
                ],
              };
            });
          })
          .finally(() => {
            setProductSummaryDependentInsights((prevData: any) => {
              return {
                ...prevData,
                missingProductInsight: false,
              };
            });
          })
          .catch(() => {
            updateEmptyComplementaryProducts();
          });
      } else {
        setProductSummaryDependentInsights((prevData: any) => {
          return {
            ...prevData,
            missingProductInsight: false,
          };
        });
      }
    },
    [
      categoryKeys,
      dataExtendedMutation,
      styledLink,
      translate,
      updateEmptyComplementaryProducts,
      insightsMessages,
      setInsightsDetails,
    ]
  );

  const getTopCategoryInsight = useCallback(
    (categoryDetails: ObjectMap[]) => {
      if (Array.isArray(categoryDetails) && categoryDetails?.length > 0) {
        let topCategoryDetails: ObjectMap = categoryDetails[0];
        const heading = translate(
          enabledInsightsVersion === 'v4'
            ? 'Insights.CommunityTrends'
            : 'Insights.TopCategory'
        );
        categoryDetails?.forEach((vendorDetails: ObjectMap) => {
          if (
            topCategoryDetails?.mdg_segment_category_rank >
            vendorDetails?.mdg_segment_category_rank
          ) {
            topCategoryDetails = vendorDetails;
          }
        });

        const categoryName = topCategoryDetails?.category ?? '';
        const category = toPascal(categoryName);
        const encodedCategoryName = encodeURIComponent(categoryName);

        const categoryUrl = `/cep/app/product/productsearch?displaytitle=${encodedCategoryName}&keywords=${encodedCategoryName}`;
        const dataTagName =
          enabledInsightsVersion === 'v4' ? 'Community-Trends' : 'Top-Category';
        const data = (
          <>
            {translate(
              enabledInsightsVersion === 'v4'
                ? 'Insights.CommunityTrendsCard'
                : 'Insights.TopCategoryInsightCard',
              {
                category: `${styledLink}<a class='styledLink' 
                data-name= 'Quick-insights_Popup_${dataTagName}_${category?.replace(
                  / /g,
                  '-'
                )}'
                data-recommendationrecordid = ${
                  insightsMessages?.recommendationRecordId
                }
                data-linktype = 'productCategory'
                data-insight="${
                  enabledInsightsVersion === 'v4'
                    ? 'Community trends'
                    : 'Vendor trends'
                }"
                href="${categoryUrl}">${category}</a>`,
              },
              true
            )}
          </>
        );

        return {
          category: categoryKeys.trendingCategory,
          description: data,
          digitalData: {
            InsightCategory: [topCategoryDetails?.category],
            InsightName:
              enabledInsightsVersion === 'v4'
                ? 'Community trends'
                : 'Top Category',
            InsightOrderhistory: '',
            InsightProduct: [],
            InsightSearchTerms: [],
            InsightVendor: [],
          },
          pages: 'Home Page, Insights Center, Details Page, Product Category',
          title: heading,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, styledLink, insightsMessages]
  );

  const updateTrendingSearches = useCallback(
    (trendingSearchList: ObjectMap[]) => {
      if (
        trendingSearchList &&
        Array.isArray(trendingSearchList) &&
        trendingSearchList?.length > 0
      ) {
        let navlinkData = '';
        const heading = translate(
          enabledInsightsVersion === 'v4'
            ? 'Insights.CommunityActivity'
            : 'Insights.TrendingProductSearchTerms'
        );
        let topTrendingProductDetails: ObjectMap = trendingSearchList[0];
        trendingSearchList?.forEach((product: ObjectMap) => {
          if (topTrendingProductDetails?.rank > product?.rank) {
            topTrendingProductDetails = product;
          }
        });

        const trendingTopSearchList =
          enabledInsightsVersion === 'v4'
            ? trendingSearchList?.slice(0, 2)
            : trendingSearchList;
        const trendingListLength = trendingTopSearchList?.length ?? 0;

        const dataTagName =
          enabledInsightsVersion === 'v4'
            ? 'Community-Activity'
            : 'Trending-Product-Search-Terms';

        const trendingSearchesDigitalData: ObjectMap = {
          InsightCategory: [],
          InsightName:
            enabledInsightsVersion === 'v4'
              ? 'Community activity'
              : 'Trending Product Search Terms',
          InsightOrderhistory: '',
          InsightProduct: [],
          InsightSearchTerms: [],
          InsightVendor: [],
        };

        trendingTopSearchList.forEach((item: ObjectMap, index: number) => {
          const trendingSearchTerm: string = item?.search_terms ?? '';

          trendingSearchesDigitalData.InsightSearchTerms?.push(
            trendingSearchTerm
          );

          const encodedTrendingSearchTerm =
            encodeURIComponent(trendingSearchTerm);
          const trendingSearchTermUrl = `/cep/app/product/productsearch?displaytitle=${encodedTrendingSearchTerm}&keywords=${encodedTrendingSearchTerm}`;
          let delimiter = '';

          if (index === trendingListLength - 2) {
            delimiter = ` ${translateKey('Insights.And')}`;
          } else if (index < trendingListLength - 2) {
            delimiter = ',';
          }

          navlinkData += `${styledLink}<a class='styledLink' 
          data-name = 'Quick-insights_Popup_${dataTagName}_${trendingSearchTerm}' 
          data-recommendationrecordid = ${
            insightsMessages?.recommendationRecordId
          }
          data-linktype = 'searchTerm'
          data-insight="${
            enabledInsightsVersion === 'v4'
              ? 'Community activity'
              : 'Trending product search terms'
          }"

          href="${trendingSearchTermUrl}">${trendingSearchTerm}</a>${delimiter}`;
        });

        const trendingData = (
          <>
            {translate(
              enabledInsightsVersion === 'v4'
                ? 'Insights.CommunityActivityCard'
                : 'Insights.TopTrendingSearchesCard',
              { value: navlinkData },
              true
            )}
          </>
        );

        setInsightsDetails((prevInsightsDetails: ObjectMap) => {
          return {
            ...prevInsightsDetails,
            [categoryKeys.trendingProductSearch]: [
              {
                category: 'Trending Searches',
                description: trendingData,
                digitalData: trendingSearchesDigitalData,
                pages: 'Home Page, Insights Center, Search Page',
                title: heading,
              },
            ],
          };
        });
      }
    },

    [
      translate,
      translateKey,
      categoryKeys,
      enabledInsightsVersion,
      styledLink,
      insightsMessages,
      setInsightsDetails,
    ]
  );

  const getSalesPercentage = useCallback(
    (sales: number) => {
      if (Math.abs(sales) < 194.99) {
        return `${parseFloat(sales.toPrecision(2))}%`;
      } else if (Math.abs(sales) >= 195 && Math.abs(sales) < 1000) {
        return `${parseFloat((sales / 100).toPrecision(2))}X`;
      } else if (sales >= 1000) {
        return `${translateKey('Insights.MoreThanKey')} <b>1000X</b>`;
      } else if (sales <= -1000) {
        return `${translateKey('Insights.LessThanKey')} <b>-1000X</b>`;
      }
    },
    [translateKey]
  );

  const updateTopCategoriesInsight = useCallback(
    (topCategoriesInsightDetails: ObjectMap[]) => {
      if (
        Array.isArray(topCategoriesInsightDetails) &&
        topCategoriesInsightDetails?.length > 0
      ) {
        let partNumbers = '';

        topCategoriesInsightDetails?.forEach((categoryDetails: ObjectMap) => {
          const partNumber = categoryDetails?.sku?.substring(2) ?? '';
          partNumbers += `{'partNumber':'${partNumber}'},`;
        });

        const skusList = `[${partNumbers.slice(0, -1)}]`;

        if (skusList === '[]') {
          setInsightsDetails((prevInsightsDetails: ObjectMap) => {
            return {
              ...prevInsightsDetails,
              [categoryKeys.trendingProductCategory]: [],
            };
          });

          return;
        }

        dataExtendedMutation
          .mutateAsync(skusList)
          .then((productSummaryResponse: any) => {
            const productSummaries = productSummaryResponse?.summaries;

            if (!productSummaries) {
              setInsightsDetails((prevInsightsDetails: ObjectMap) => {
                return {
                  ...prevInsightsDetails,
                  [categoryKeys.trendingProductCategory]: [],
                };
              });

              return;
            }

            const skusMap: ObjectMap = getSKUMap(productSummaries);
            const filteredProductDetails: Array<ObjectMap> = [];

            topCategoriesInsightDetails?.forEach(
              (categoryDetails: ObjectMap) => {
                const partNumber = categoryDetails?.sku
                  ?.substring(2)
                  .replace(/^0+/, '');

                if (Object.prototype.hasOwnProperty.call(skusMap, partNumber)) {
                  filteredProductDetails.push({
                    ...categoryDetails,
                    isAuthorizedToBuy:
                      skusMap[partNumber]?.authorizedToPurchase,
                    productSummary: skusMap[partNumber],
                  });
                }
              }
            );

            const sortedCategoryDetailsByRank = sortJSONListByKey(
              filteredProductDetails,
              'sku_rank'
            );
            const sortedDataByAuthorizationToBuy = sortJSONListByBooleanKey(
              sortedCategoryDetailsByRank,
              'isAuthorizedToBuy',
              'desc'
            );

            if (sortedDataByAuthorizationToBuy?.length === 0) {
              setInsightsDetails((prevInsightsDetails: ObjectMap) => {
                return {
                  ...prevInsightsDetails,
                  [categoryKeys.trendingProductCategory]: [],
                };
              });

              return;
            }

            const topCategoryDetails =
              sortedDataByAuthorizationToBuy?.[0] ?? {};
            const heading = translate(
              enabledInsightsVersion === 'v4'
                ? 'Insights.MarketTrends'
                : 'Insights.TopCategories'
            );
            const partNumber = topCategoryDetails?.productSummary?.partnumber;

            const categoryName = topCategoryDetails?.category ?? '';
            const dataCategoryName = topCategoryDetails?.category?.replace(
              / /g,
              '-'
            );
            const category = toPascal(categoryName);
            const encodedDataCategoryName = encodeURIComponent(categoryName);

            const productdetailsLink = `/cep/app/product/productdetails?id=${partNumber}`;
            const searchLink = `/cep/app/product/productsearch?displaytitle=${encodedDataCategoryName}&keywords=${encodedDataCategoryName}`;
            const dataName =
              enabledInsightsVersion === 'v4'
                ? 'MarketTrends'
                : 'TopCategories';
            const insightType =
              enabledInsightsVersion === 'v4'
                ? 'Market trends'
                : 'Top categories';
            const data = (
              <>
                {translate(
                  'Insights.Card_TopProductGrowth_Text',
                  {
                    category: searchLink
                      ? `${styledLink}<a class='styledLink' data-engineid='engine_${dataCategoryName}_Insight_${dataName}_link' 
                      data-name = 'Quick-insights_Popup_${dataName}_${dataCategoryName}'
                      data-recommendationrecordid =${insightsMessages?.recommendationRecordId}
                      data-linktype = 'productCategory'
                      data-insight= "${insightType}"

                      data-testid = '${dataCategoryName}_Insight_${dataName}_link' 
                      href=${searchLink}>${category}</a>`
                      : ` <b>${category}</b>`,

                    percentage:
                      getSalesPercentage(
                        topCategoryDetails?.sales_growth_ratio
                      ) ?? '',

                    product: productdetailsLink
                      ? `${styledLink}<a class='styledLink' data-engineid='engine_${partNumber}_Insight_${dataName}_link' 
                      data-name = 'Quick-insights_Popup_${dataName}_${topCategoryDetails?.product_description?.replace(
                          / /g,
                          '-'
                        )}' 
                      data-recommendationrecordid = ${
                        insightsMessages?.recommendationRecordId
                      }
                      data-linktype = 'productName'
                      data-insight= "${insightType}"

                      data-testid = '${partNumber}_Insight_${dataName}_link' 
                      href=${productdetailsLink}>${
                          topCategoryDetails?.product_description
                        }</a>`
                      : ` <b>${topCategoryDetails?.product_description}</b>`,
                  },
                  true
                )}
              </>
            );

            setInsightsDetails((prevInsightsDetails: ObjectMap) => {
              return {
                ...prevInsightsDetails,
                [categoryKeys.trendingProductCategory]: [
                  {
                    category: categoryKeys.trendingProductCategory,
                    description: data,
                    digitalData: {
                      InsightCategory: [],
                      InsightName: dataName,
                      InsightOrderhistory: '',
                      InsightProduct: [topCategoryDetails?.product_description],
                      InsightSearchTerms: [],
                      InsightVendor: [],
                    },
                    pages: 'Insights Center',
                    title: heading,
                  },
                ],
              };
            });
          })
          .finally(() => {
            setProductSummaryDependentInsights((prevData: any) => {
              return {
                ...prevData,
                topCategoryInsight: false,
              };
            });
          });
      } else {
        setProductSummaryDependentInsights((prevData: any) => {
          return {
            ...prevData,
            topCategoryInsight: false,
          };
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, dataExtendedMutation, getSalesPercentage]
  );

  const getTopCategoryInLast30Days = useCallback(
    (topCategoriesInLast30DaysInsightDetails: ObjectMap[]) => {
      if (
        Array.isArray(topCategoriesInLast30DaysInsightDetails) &&
        topCategoriesInLast30DaysInsightDetails?.length > 0
      ) {
        const heading = translate(
          enabledInsightsVersion === 'v4'
            ? 'Insights.YourTrends'
            : 'Insights.TrendingCategory'
        );
        const categoryName =
          topCategoriesInLast30DaysInsightDetails?.[0]?.category ?? '';
        const dataCategoryName = categoryName?.replace(/ /g, '-');
        const category = toPascal(categoryName);
        const encodedDataCategory = encodeURIComponent(categoryName ?? '');
        const categoryLink = `/cep/app/product/productsearch?displaytitle=${encodedDataCategory}&keywords=${encodedDataCategory}`;
        const insightType =
          enabledInsightsVersion === 'v4' ? 'Your trends' : 'Trending category';
        const data = (
          <>
            {translate(
              enabledInsightsVersion === 'v4'
                ? 'Insights.TopCategoryGrowthAndProduct_CardContent'
                : 'Insights.TopCategoryGrowth_last30Days',
              {
                category: `${styledLink}<a class='styledLink' data-engineid='engine_${dataCategoryName}_Insight_TrendingProducts' 
                data-name = 'Quick-insights_Popup_Trending-Category_${dataCategoryName}' data-recommendationrecordid = ${insightsMessages?.recommendationRecordId} 
                data-linktype = 'productCategory' data-insight="${insightType}" data-testid = '${categoryName}_Insight_TrendingProducts_link' href=${categoryLink}>${category}</a>`,
              },
              true
            )}
          </>
        );

        return {
          category: categoryKeys.trendingCategory,
          description: data,
          digitalData: {
            InsightCategory: [categoryName],
            InsightName:
              enabledInsightsVersion === 'v4'
                ? 'Your trends'
                : categoryKeys.trendingCategory,
            InsightOrderhistory: '',
            InsightProduct: [],
            InsightSearchTerms: [],
            InsightVendor: [],
          },
          pages: 'Insights Center',
          title: heading,
        };
      }
    },
    [
      styledLink,
      categoryKeys,
      translate,
      insightsMessages,
      enabledInsightsVersion,
    ]
  );

  const updateTrendingProductsInsights = useCallback(
    (topTrendingProductsDetails: ObjectMap[]) => {
      if (
        Array.isArray(topTrendingProductsDetails) &&
        topTrendingProductsDetails?.length > 0
      ) {
        let partNumbers = '';

        topTrendingProductsDetails?.forEach((trendingDetails: ObjectMap) => {
          const partNumber = trendingDetails?.sku ?? '';
          partNumbers += `{'partNumber':'${partNumber}'},`;
        });

        const skusList = `[${partNumbers.slice(0, -1)}]`;

        if (skusList === '[]') {
          setInsightsDetails((prevInsightsDetails: ObjectMap) => {
            return {
              ...prevInsightsDetails,
              [categoryKeys.trendingProduct]: [],
            };
          });

          return;
        }

        dataExtendedMutation
          .mutateAsync(skusList)
          .then((productSummaryResponse: any) => {
            const productSummaries = productSummaryResponse?.summaries;

            if (!productSummaries) {
              setInsightsDetails((prevInsightsDetails: ObjectMap) => {
                return {
                  ...prevInsightsDetails,
                  [categoryKeys.trendingProduct]: [],
                };
              });

              return;
            }

            const skusMap: ObjectMap = getSKUMap(productSummaries);
            const filteredProductDetails: Array<ObjectMap> = [];

            topTrendingProductsDetails?.forEach(
              (trendingDetails: ObjectMap) => {
                const partNumber = trendingDetails?.sku;

                filteredProductDetails.push({
                  ...trendingDetails,
                  isAuthorizedToBuy: skusMap[partNumber]?.authorizedToPurchase,
                  productSummary: skusMap[partNumber] ?? {},
                });
              }
            );

            const sortedDataByAuthorizationToBuy = sortJSONListByBooleanKey(
              filteredProductDetails,
              'isAuthorizedToBuy',
              'desc'
            );
            const sortedTrendingDetailsByRank = sortJSONListByKey(
              sortedDataByAuthorizationToBuy,
              'sku_rank'
            );
            const sortbyNullArray = sortJSONByNullValue(
              sortedTrendingDetailsByRank,
              'productSummary'
            );

            if (sortbyNullArray?.length === 0) {
              setInsightsDetails((prevInsightsDetails: ObjectMap) => {
                return {
                  ...prevInsightsDetails,
                  [categoryKeys.trendingProduct]: [],
                };
              });

              return;
            }

            const topTrendingDetails = sortbyNullArray?.[0] ?? {};
            const partNumber = topTrendingDetails?.productSummary?.partnumber;
            const topProductUrl = `/cep/app/product/productdetails?id=${partNumber}`;
            const heading = translate(
              enabledInsightsVersion === 'v4'
                ? 'Insights.CommunityTrends'
                : 'Insights.TrendingProducts'
            );
            const insightType =
              enabledInsightsVersion === 'v4'
                ? 'Community trends'
                : 'Trending products';
            const firstProduct = sortbyNullArray?.[0] ?? {};
            const firstProductSku = firstProduct?.productSummary?.partnumber;

            const firstProductUrl = firstProductSku
              ? `/cep/app/product/productdetails?id=${firstProductSku}`
              : undefined;

            const secondProduct = sortbyNullArray?.[1] ?? {};
            const secondProductSku = secondProduct?.productSummary?.partnumber;

            const secondProductUrl = secondProductSku
              ? `/cep/app/product/productdetails?id=${secondProductSku}`
              : undefined;

            const data = (
              <>
                {enabledInsightsVersion === 'v4'
                  ? translate(
                      'Insights.Customer_HighGrowth_TrendingProducts',
                      {
                        highGrowth: firstProductUrl
                          ? `${styledLink}<a class='styledLink' data-engineid='engine_${firstProductSku}_Insight_CommunityTrends' 
                    data-name = 'Quick-insights_Popup_CommunityTrends-products_${firstProduct?.product_description?.replace(
                      /\s+/g,
                      '-'
                    )}' 
                      data-recommendationrecordid = ${
                        insightsMessages?.recommendationRecordId
                      }
                      data-linktype = 'productName'                  
                      data-insight="${insightType}"
                      data-testid = '${firstProductSku}_Insight_CommunityTrends_link' href=${firstProductUrl}>${
                              firstProduct?.product_description
                            }</a>`
                          : `<b>${firstProduct?.product_description}</b>`,

                        lowGrowth: secondProductUrl
                          ? `${styledLink}<a class='styledLink' data-engineid='engine_${secondProductSku}_Insight_CommunityTrends' 
                    data-name = 'Quick-insights_Popup_CommunityTrends_${secondProduct?.product_description?.replace(
                      /\s+/g,
                      '-'
                    )}' 
                      data-recommendationrecordid = ${
                        insightsMessages?.recommendationRecordId
                      }
                      data-linktype = 'productName'
                      data-insight="${insightType}"
                      data-testid = '${secondProductSku}_Insight_CommunityTrends_link' href=${secondProductUrl}>${
                              secondProduct?.product_description
                            }</a>`
                          : `<b>${secondProduct?.product_description}</b>`,
                      },
                      true
                    )
                  : translate(
                      'Insights.Trending_Product_Top_Product_In_Top_Category',
                      {
                        topProduct: topProductUrl
                          ? `${styledLink}<a class='styledLink' data-engineid='engine_${partNumber}_Insight_TrendingProducts' 
                      data-name = 'Quick-insights_Popup_Trending-products_${topTrendingDetails?.product_description?.replace(
                        /\s+/g,
                        '-'
                      )}' 
                        data-recommendationrecordid = ${
                          insightsMessages?.recommendationRecordId
                        } 
                        data-linktype = 'productName'
                        data-insight="${insightType}"
                        data-testid = '${partNumber}_Insight_TrendingProducts_link' href=${topProductUrl}>${
                              topTrendingDetails?.product_description
                            }</a>`
                          : `<b>${topTrendingDetails?.product_description}</b>`,
                      },
                      true
                    )}
              </>
            );

            setInsightsDetails((prevInsightsDetails: ObjectMap) => {
              return {
                ...prevInsightsDetails,
                [categoryKeys.trendingProduct]: [
                  {
                    category: categoryKeys.trendingProduct,
                    description: data,
                    digitalData: {
                      InsightCategory: [],
                      InsightName: 'Trending products',
                      InsightOrderhistory: '',
                      InsightProduct: [topTrendingDetails?.product_description],
                      InsightSearchTerms: [],
                      InsightVendor: [],
                    },
                    pages: 'Home Page, Insights Center, Search Page',
                    title: heading,
                  },
                ],
              };
            });
          })
          .finally(() => {
            setProductSummaryDependentInsights((prevData: any) => {
              return {
                ...prevData,
                trendingProductInsight: false,
              };
            });
          });
      } else {
        setProductSummaryDependentInsights((prevData: any) => {
          return {
            ...prevData,
            trendingProductInsight: false,
          };
        });
      }
    },
    [
      categoryKeys,
      enabledInsightsVersion,
      translate,
      dataExtendedMutation,
      styledLink,
      insightsMessages,
      setInsightsDetails,
    ]
  );

  const updateEfficientTipsInsight = useCallback(
    (isResellerWhitelistedForCTO: boolean) => {
      if (
        enableEfficiencyTipsInsightsIsReady &&
        enableEfficiencyTipsInsights === 'true' &&
        isResellerWhitelistedForCTO &&
        isConfigureToOrderUrlReady &&
        isConfigureToOrderQuestionsUrlReady
      ) {
        const heading = translate('Insights.EfficientTips');
        const configureToOrder = translateKey('Insights.ConfigureToOrder');
        const questions = translateKey('Insights.EfficientTipsQuestions');
        const data = (
          <>
            {translate(
              'Insights.EfficientTipsInsight',
              {
                ctoLink: `${styledLink}<a class='styledLink' data-engineid='engine_Insight_EfficienyTips' 
                  data-name = 'Quick-insights_Popup_EfficienyTips_Configure-To-Order' 
                  data-recommendationrecordid = ${insightsMessages?.recommendationRecordId}
                  data-linktype = 'configureToOrder'
                  data-insight= 'Efficiency tips'
                  data-testid = 'Configure-To-Order_Insight_EfficienyTips_link' href=${configureToOrderUrl}>${configureToOrder}</a>`,
                questions: `${styledLink}<a class='styledLink' data-engineid='engine_Insight_EfficienyTips' 
                  data-name = 'Quick-insights_Popup_EfficienyTips_Questions' 
                  data-recommendationrecordid = ${insightsMessages?.recommendationRecordId}
                  data-insight= 'Efficiency tips'
                  data-linktype = 'EfficiencyTipsQuestions'
                  data-testid = 'Questions_Insight_EfficienyTips_link' href=${configureToOrderQuestionsUrl}>${questions}</a>`,
              },
              true
            )}
          </>
        );

        const InsightDetails = {
          category: categoryKeys.efficientTips,
          description: data,
          digitalData: {
            InsightCategory: [],
            InsightName: 'Efficieny Tips',
            InsightOrderhistory: '',
            InsightProduct: [],
            InsightSearchTerms: [],
            InsightVendor: [],
          },
          pages:
            'Home Page, Insights Center, cart checkout, Search Page, Details Page, Product Category, Vendor List',
          title: heading,
        };
        setInsightsDetails((prevInsightsDetails: ObjectMap) => {
          return {
            ...prevInsightsDetails,
            [categoryKeys.efficientTips]: [InsightDetails],
          };
        });
      }
    },
    [
      categoryKeys,
      configureToOrderQuestionsUrl,
      configureToOrderUrl,
      enableEfficiencyTipsInsights,
      enableEfficiencyTipsInsightsIsReady,
      insightsMessages?.recommendationRecordId,
      isConfigureToOrderQuestionsUrlReady,
      isConfigureToOrderUrlReady,
      setInsightsDetails,
      styledLink,
      translate,
      translateKey,
    ]
  );

  const updateInsightDetails = useCallback(
    (insightsMessages: { [x: string]: any }) => {
      updateEfficientTipsInsight(
        insightsMessages?.['IsResellerWhitelistedForCTOStaticInsights'] ?? false
      );
      if (
        userRole !== userRoles.BASIC ||
        !enableUserManagement ||
        enableUserManagement !== 'true'
      ) {
        const salesFrequencyInsight = getSalesFrequencyInsight(
          JSON.parse(
            insightsMessages?.[
              'x4c_insights_combined.insight__order_count_ytd'
            ] ?? '{}'
          )
        );

        if (salesFrequencyInsight) {
          setInsightsDetails((prevInsightsDetails: ObjectMap) => {
            return {
              ...prevInsightsDetails,
              [categoryKeys.accountInfo]: [salesFrequencyInsight],
            };
          });
        }
      }

      const categoryByVendorInsight = getCategoryByVendorInsight(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insight__top_vendor_in_top_category'
          ] ?? '{}'
        )
      );

      const topCategoryInsight = getTopCategoryInsight(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insights__top_categories_in_mdg_segment'
          ] ?? '[]'
        )
      );

      const topCategory_30Days = getTopCategoryInLast30Days(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insight__top_category_growth_with_top_product_sales'
          ] ?? '[]'
        )
      );

      updateTopVendorInsight(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insight__top_vendor_growth_with_top_product_sales'
          ] ?? '[]'
        )
      );

      updateMissingProductInBasket(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insight__missing_product_market_basket'
          ] ?? '[]'
        )
      );

      updateTrendingSearches(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insight__trending_searches_in_mdg_segment'
          ] ?? '[]'
        )
      );

      updateTopCategoriesInsight(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insight__top_product_growth_top_category'
          ] ?? '[]'
        )
      );

      updateTrendingProductsInsights(
        JSON.parse(
          insightsMessages?.[
            'x4c_insights_combined.insights__trending_products_growth'
          ] ?? '[]'
        )
      );

      if (categoryByVendorInsight) {
        setInsightsDetails((prevInsightsDetails: ObjectMap) => {
          return {
            ...prevInsightsDetails,
            [categoryKeys.trendingVendor]: [categoryByVendorInsight],
          };
        });
      }

      const trendingCategoryInsights: ObjectMap[] = [];
      [topCategoryInsight, topCategory_30Days]?.forEach((insight: any) => {
        if (insight) trendingCategoryInsights.push(insight);
      });

      if (trendingCategoryInsights?.length > 0) {
        setInsightsDetails((prevInsightsDetails: ObjectMap) => {
          return {
            ...prevInsightsDetails,
            [categoryKeys.trendingCategory]: trendingCategoryInsights,
          };
        });
      }
    },
    [
      updateEfficientTipsInsight,
      userRole,
      enableUserManagement,
      getCategoryByVendorInsight,
      getTopCategoryInsight,
      getTopCategoryInLast30Days,
      updateTopVendorInsight,
      updateMissingProductInBasket,
      updateTrendingSearches,
      updateTopCategoriesInsight,
      updateTrendingProductsInsights,
      getSalesFrequencyInsight,
      setInsightsDetails,
      categoryKeys,
    ]
  );

  useEffect(() => {
    if (!insightsMessagesLoading && allInsightsLoaded) {
      setDigitalData({
        QuickInsightsImpressions: chipsDetails?.[0]?.digitalData ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInsightsLoaded]);

  useEffect(() => {
    if (insightsDetails !== null) {
      updateChipsDetails(
        currentPage,
        pageWiseCagetoriesDetails?.[currentPage],
        insightsDetails
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageWiseCagetoriesDetails, insightsDetails, currentPage]);

  useEffect(() => {
    if (
      !productSummaryDependentInsights?.missingProductInsight &&
      !productSummaryDependentInsights?.topCategoryInsight &&
      !productSummaryDependentInsights?.topVendorInsight &&
      !productSummaryDependentInsights?.trendingProductInsight
    )
      setAllInsightsLoaded(true);
  }, [productSummaryDependentInsights]);

  useEffect(() => {
    if (insightsMessagesLoading) return;

    if (insightsMessages?.items?.length > 0) {
      updateInsightDetails(insightsMessages?.items?.[0]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightsMessagesLoading, insightsMessages?.items]);

  useEffect(() => {
    if (insightsDetails !== null) {
      setAllInsightsLoaded(true);
      return;
    }

    setInsightsMessagesLoading(true);
    insightsMutation.mutateAsync(combinedInsights).then((response) => {
      setInsightsMessagesLoading(false);
      setInsightsMessages(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightsDetails]);

  return (
    <Box component="div" py={2} sx={{ maxWidth: 650 }}>
      {(insightsMessagesLoading || !allInsightsLoaded) && (
        <InsightsMessagingQueueSkeleton />
      )}
      <StyledStack
        alignItems="center"
        direction="row"
        flexWrap="nowrap"
        spacing={1}
      >
        {!insightsMessagesLoading &&
          allInsightsLoaded &&
          chipsDetails?.map((chipDetails: ObjectMap) => {
            return (
              <StyledChip
                className={chipDetails?.active && 'active'}
                data-name={chipDetails?.dataName}
                deleteIcon={
                  <CountBadge className="count-badge">
                    {chipDetails?.insightsCount ?? 0}
                  </CountBadge>
                }
                key={chipDetails?.title}
                label={chipDetails?.title}
                onClick={() => showContent(chipDetails)}
                onDelete={onCountClick}
                variant="outlined"
              />
            );
          })}
      </StyledStack>
      {!insightsMessagesLoading &&
        chipsDetails?.length <= 1 &&
        allInsightsLoaded && (
          <Typography pl={8} pt={6} variant="h2">
            {translate('Insights.NoInsightsAvailable')}
          </Typography>
        )}
      {!insightsMessagesLoading &&
        allInsightsLoaded &&
        enabledInsightsVersionIsReady && (
          <Stack direction="row" flexWrap="wrap" spacing={1}>
            {chipsDetails?.map((chipDetails: ObjectMap) => {
              return chipDetails?.active && <>{chipDetails?.content}</>;
            })}
          </Stack>
        )}
    </Box>
  );
}

function toPascal(string: string) {
  const convertedString = string.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
    return g1.toUpperCase() + g2.toLowerCase();
  });
  return convertedString;
}

const StyledStack = styled(Stack)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '8.86px',
    width: '8.86px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.text.disabled,
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.primary.main,
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '6px',
    boxShadow: 'none',
  },
  '&::-webkit-scrollbar-track:hover': {
    boxShadow: 'inset 0 0 6px #C4C4C4',
  },
  height: 46,
  overflowX: 'auto',
  overflowY: 'hidden',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  '&.active': {
    '.count-badge': {
      backgroundColor: 'rgba(1, 100, 176, 1)',
      color: '#fff',
    },
    backgroundColor: 'rgba(243, 250, 255, 1)',
    borderColor: 'rgba(0, 119, 212, 1)',
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
}));

const CountBadge = styled(Box)(({ theme }) => ({
  '&.MuiChip-deleteIcon, &.MuiChip-deleteIcon:hover': {
    backgroundColor: '#eeeeee',
    borderRadius: 4,
    color: '#333333',
    fontSize: '12px !important',
    padding: theme.spacing(0, 1, 0, 1),
  },
}));

interface TemporalEnableddProps {
  isDisabled: boolean;
}

const StyledLinkTypography = styled('a')<TemporalEnableddProps>(
  ({ isDisabled }) => ({
    color: isDisabled ? '#424242' : '#0077D4',
    cursor: isDisabled ? 'default' : 'pointer',
    display: 'inline',
    fontSize: '16px',
    fontWeight: '600',
    minWidth: 0,
    overflow: 'hidden',
    textDecoration: 'none',
    textOverflow: 'ellipsis',
  })
);
